import {
  GenerateEmailRequestParamsI,
  GenerateEmailResponseI,
} from "shared/lib/interfaces/email/ai";
import { fetchWithTimeout } from "shared/lib/helpers/http";
import { TIME_INTERVALS } from "shared/lib/constants/time";

import { StreamAPIConfig } from "@/api/stream";

/**
 * Longer than the shared client timeout, which is 20s in production.
 *
 * One generation now covers both subject and body. With the rep's call in
 * hand the model runs a single turn and answers quickly, but with no call it
 * keeps the web search tool and may take up to four turns plus a style
 * revision pass. 20s would abort a run that was going to succeed, and the rep
 * would see a failure with a draft on the way.
 */
export const AI_EMAIL_GENERATION_TIMEOUT_MS = 60 * TIME_INTERVALS.ONE_SECOND;

export const EmailWebAIAPIEndpoints = (API: StreamAPIConfig) => ({
  /**
   * Subject and body from one model call.
   *
   * Replaces the `generate_content_body` + `generate_content_subject` pair
   * this app used to call in parallel. Those endpoints still exist server-side
   * for glenx and the customer app, which keep their own clients.
   */
  generateEmail: async (
    parameters: GenerateEmailRequestParamsI,
    signal?: AbortSignal
  ): Promise<GenerateEmailResponseI> => {
    const response = await fetchWithTimeout(
      `${API.baseURL}/email/generate`,
      {
        method: "POST",
        headers: API.headers,
        body: JSON.stringify(parameters),
      },
      AI_EMAIL_GENERATION_TIMEOUT_MS,
      signal
    );

    if (!response.ok) {
      /**
       * The body is logged, never shown. A 502 from the gateway is an HTML
       * error page and an upstream vendor error stringifies to its whole
       * response, so putting the raw text in the thrown message - which the
       * caller toasts verbatim - filled a rep's screen with markup or with
       * request and org identifiers.
       */
      const errorText = await response.text().catch(() => "");

      // eslint-disable-next-line no-console
      console.error("[email/generate]", response.status, errorText);

      // 403 is the campaign authorization guard, not a transient fault, so it
      // does not get "please try again" - retrying will fail the same way.
      if (response.status === 403) {
        throw new Error("You do not have access to this campaign.");
      }

      throw new Error(
        `Could not generate the email (${response.status}). Please try again.`
      );
    }

    return response.json();
  },
});
